import { z } from 'zod';
import { BOOKING_STATUSES } from '@bulk/types';
import { dateTimeSchema, idSchema, publicAssetSchema, searchQuerySchema, sortOrderSchema } from './common';
import { bookingRefusalSchema } from './classes';

export const createBookingSchema = z.object({
  sessionId: idSchema,
  memberId: idSchema.optional(),
});
export type CreateBookingInput = z.infer<typeof createBookingSchema>;

export const bookingSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  sessionId: idSchema,
  memberId: idSchema,
  memberName: z.string(),
  membershipId: idSchema.nullable(),
  packageId: idSchema.nullable().describe('The class pack the booking took a visit from; null when a membership covered it or it is waitlisted'),
  status: z.enum(BOOKING_STATUSES),
  waitlistPosition: z.number().int().nullable(),
  bookedAt: z.string(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  attendedAt: z.string().nullable(),
  className: z.string(),
  sessionStartsAt: z.string(),
  sessionEndsAt: z.string(),
  trainerName: z.string().nullable(),
  trainerPhoto: publicAssetSchema.nullable(),
  location: z.string().nullable(),
  locationName: z.string().describe('The branch the session happens at'),
});
export type BookingDto = z.infer<typeof bookingSchema>;

export const BOOKING_LIST_SORTS = ['member', 'class', 'session', 'trainer', 'status', 'bookedAt'] as const;

export const bookingListQuerySchema = searchQuerySchema.extend({
  q: searchQuerySchema.shape.q.describe("Part of the member's name or number, the class name or the trainer's name"),
  status: z.enum(BOOKING_STATUSES).optional(),
  sessionId: idSchema.optional(),
  memberId: idSchema.optional(),
  classId: idSchema.optional(),
  trainerId: idSchema.optional(),
  from: dateTimeSchema.optional().describe('Only sessions starting at or after this moment'),
  to: dateTimeSchema.optional().describe('Only sessions starting at or before this moment'),
  upcoming: z.stringbool().optional(),
  sort: z.enum(BOOKING_LIST_SORTS).default('session').describe('`member` and `trainer` are last name, then first name; `session` is the session start; empty values come last in either direction'),
  order: sortOrderSchema.optional().describe('Defaults to soonest first with `upcoming`, latest first otherwise'),
});
export type BookingListQuery = z.infer<typeof bookingListQuerySchema>;

export const cancelBookingSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

export const markAttendanceSchema = z.object({
  bookingIds: z.array(idSchema).min(1).max(500),
  status: z.enum(['ATTENDED', 'NO_SHOW']),
});
export type MarkAttendanceInput = z.infer<typeof markAttendanceSchema>;

export const attendanceSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  memberId: idSchema,
  sessionId: idSchema.nullable(),
  bookingId: idSchema.nullable(),
  recordedByUserId: idSchema.nullable(),
  source: z.enum(['STAFF', 'TRAINER', 'ACCESS_EVENT', 'MEMBER']),
  recordedAt: z.string(),
});
export type AttendanceDto = z.infer<typeof attendanceSchema>;

export const bookingEligibilityQuerySchema = z.object({
  memberId: idSchema,
  sessionIds: z
    .string()
    .transform((value) => value.split(',').filter(Boolean))
    .pipe(z.array(idSchema).max(100))
    .describe('Comma-separated session ids'),
});
export type BookingEligibilityQuery = z.infer<typeof bookingEligibilityQuerySchema>;

export const bookingEligibilitySchema = z.object({
  sessionId: idSchema,
  refusal: bookingRefusalSchema.nullable().describe('Why staff could not book the member into this session; null when they can'),
  classPackId: idSchema.nullable().describe('The class pack booking would take a visit from; null when a membership covers the session or it cannot be booked'),
});
export type BookingEligibilityDto = z.infer<typeof bookingEligibilitySchema>;

export const classPackSchema = z.object({
  id: idSchema,
  memberId: idSchema,
  planId: idSchema.nullable(),
  planName: z.string().nullable(),
  planNameLocalized: z.record(z.string(), z.string()),
  orderId: idSchema.nullable(),
  classes: z.array(z.object({ id: idSchema, name: z.string(), nameLocalized: z.record(z.string(), z.string()) })).describe('The classes its visits book, as sold'),
  visitsTotal: z.number().int(),
  balance: z.number().int().describe('Visits not yet booked'),
  startsAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  usable: z.boolean().describe('Visits left, not expired and not taken back by a refund'),
  createdAt: z.string(),
});
export type ClassPackDto = z.infer<typeof classPackSchema>;

export const classPackQuerySchema = z.object({
  memberId: idSchema.optional().describe("Staff with bookings.read name the member; a member reads their own"),
});
export type ClassPackQuery = z.infer<typeof classPackQuerySchema>;
